// apps/widget/src/components/radar-chart.tsx
//
// The shape of someone's skin at a glance. Capped at 8 axes — beyond that the
// polygon is unreadable and the labels collide.
//
// A table view toggle sits alongside, because a radar chart is not accessible
// on its own.

import { useState } from 'react';
import { BAND_COLORS } from '../lib/theme';
import type { ReportMetric } from '../lib/api';
import { t, metricLabel } from '../lib/i18n';

// The plot is drawn in a 220 box, but the viewBox is padded on every side so
// labels outside the outer ring are never clipped. They were before: "Tear
// Trough" rendered as "ear Trough" and "Firmness" as "Firm".
const SIZE = 220;
const CENTRE = SIZE / 2;
const RADIUS = 78;
const PAD = 62;
const LABEL_GAP = 14;

export function RadarChart({ metrics }: { metrics: ReportMetric[] }) {
  const [asTable, setAsTable] = useState(false);

  // The worst eight, so the chart shows what matters rather than an arbitrary
  // alphabetical slice.
  const axes = [...metrics].sort((a, b) => a.score - b.score).slice(0, 8);
  if (axes.length < 3) return null;

  const points = axes.map((metric, i) => {
    const angle = (i / axes.length) * Math.PI * 2 - Math.PI / 2;
    const distance = (metric.score / 100) * RADIUS;
    return {
      metric,
      x: CENTRE + Math.cos(angle) * distance,
      y: CENTRE + Math.sin(angle) * distance,
      labelX: CENTRE + Math.cos(angle) * (RADIUS + LABEL_GAP),
      labelY: CENTRE + Math.sin(angle) * (RADIUS + LABEL_GAP),
    };
  });

  if (asTable) {
    return (
      <div className="gs-radar">
        <table className="gs-table">
          <tbody>
            {axes.map((m) => (
              <tr key={m.key}>
                <th scope="row">{metricLabel(m.key, m.label)}</th>
                <td>{m.score}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <button className="gs-link" onClick={() => setAsTable(false)}>
          Show chart
        </button>
      </div>
    );
  }

  return (
    <div className="gs-radar">
      <svg
        viewBox={`${-PAD} ${-PAD / 2} ${SIZE + PAD * 2} ${SIZE + PAD}`}
        className="gs-radar__svg"
        width="100%"
        role="img"
        aria-label={t('a11y.skinProfile')}
      >
        {[0.25, 0.5, 0.75, 1].map((ring) => (
          <circle
            key={ring}
            cx={CENTRE}
            cy={CENTRE}
            r={RADIUS * ring}
            fill="none"
            stroke="#f3f4f6"
            strokeWidth="1"
          />
        ))}

        {points.map((p) => (
          <line
            key={p.metric.key}
            x1={CENTRE}
            y1={CENTRE}
            x2={CENTRE + (p.x - CENTRE) * (100 / Math.max(1, p.metric.score))}
            y2={CENTRE + (p.y - CENTRE) * (100 / Math.max(1, p.metric.score))}
            stroke="#f3f4f6"
          />
        ))}

        <polygon
          points={points.map((p) => `${p.x},${p.y}`).join(' ')}
          fill="var(--gs-primary)"
          fillOpacity="0.18"
          stroke="var(--gs-primary)"
          strokeWidth="2"
        />

        {points.map((p) => (
          <circle key={p.metric.key} cx={p.x} cy={p.y} r="3" fill={BAND_COLORS[p.metric.band]} />
        ))}

        {points.map((p) => {
          const anchor =
            p.labelX > CENTRE + 4 ? 'start' : p.labelX < CENTRE - 4 ? 'end' : 'middle';
          // Two-word labels go on two lines so they stay narrow at the sides.
          const lines = metricLabel(p.metric.key, p.metric.label).split(' ');
          const top = p.labelY < CENTRE - 4;
          const bottom = p.labelY > CENTRE + 4;
          // Lines stack away from the chart: upward at the top, downward at the bottom.
          const firstDy = top ? -(lines.length) * 10 + 4 : bottom ? 4 : -((lines.length - 1) * 10) / 2;
          return (
            <text
              key={p.metric.key}
              x={p.labelX}
              y={p.labelY}
              fontSize="9.5"
              fill="#4b5563"
              textAnchor={anchor}
              dominantBaseline="middle"
            >
              {lines.map((line, i) => (
                <tspan key={i} x={p.labelX} dy={i === 0 ? firstDy : 10}>
                  {line}
                </tspan>
              ))}
              <tspan
                x={p.labelX}
                dy={10}
                fontWeight="700"
                fill={BAND_COLORS[p.metric.band]}
              >
                {p.metric.score}
              </tspan>
            </text>
          );
        })}
      </svg>

      <button className="gs-link" onClick={() => setAsTable(true)}>
        View as table
      </button>
    </div>
  );
}
